// "What's New" modal — shown once per user per release.
//
// On mount, compares localStorage["release-notes-last-seen"] to LATEST_VERSION.
// If the user has never seen any release (first ever load), we silently stamp
// the latest version without showing anything — they don't need release notes
// from before they joined. If they've seen a previous version, the modal
// surfaces every release newer than what they last saw, then stamps the new
// latest.
//
// Mounted once at the top of App.tsx so it shows regardless of which portal
// the user lands on.

import { useEffect, useState } from "react";
import { Sparkles, X, Check } from "lucide-react";
import { LATEST_VERSION, RELEASE_NOTES, notesNewerThan, type ReleaseNote } from "@/lib/releaseNotes";

const LAST_SEEN_KEY = "release-notes-last-seen";

export default function ReleaseNotesModal() {
  const [open, setOpen] = useState(false);
  const [notes, setNotes] = useState<ReleaseNote[]>([]);

  useEffect(() => {
    let lastSeen: string | null = null;
    try { lastSeen = localStorage.getItem(LAST_SEEN_KEY); } catch { /* ignore SSR / private mode */ }

    // First-ever visit: stamp the latest version silently. No dump-truck of
    // changelog for someone who just installed.
    if (!lastSeen) {
      try { localStorage.setItem(LAST_SEEN_KEY, LATEST_VERSION); } catch { /* ignore */ }
      return;
    }

    if (lastSeen === LATEST_VERSION) return;

    const fresh = notesNewerThan(lastSeen);
    if (fresh.length === 0) {
      // Edge: last-seen present but we found nothing newer (e.g. hand-edited
      // localStorage). Stamp + bail.
      try { localStorage.setItem(LAST_SEEN_KEY, LATEST_VERSION); } catch { /* ignore */ }
      return;
    }
    setNotes(fresh);
    setOpen(true);
  }, []);

  const dismiss = () => {
    try { localStorage.setItem(LAST_SEEN_KEY, LATEST_VERSION); } catch { /* ignore */ }
    setOpen(false);
  };

  if (!open || notes.length === 0) return null;

  return (
    <>
      <div className="rnm-overlay" onClick={dismiss} aria-hidden="true" />
      <div role="dialog" aria-modal="true" aria-labelledby="rnm-title" className="rnm-card">
        <div className="rnm-header">
          <span className="rnm-eyebrow">
            <Sparkles size={12} /> What's new
          </span>
          <button type="button" onClick={dismiss} aria-label="Close" className="rnm-close">
            <X size={16} />
          </button>
        </div>

        <h2 id="rnm-title" className="rnm-title">
          {notes[0]!.title}
        </h2>
        {notes[0]!.summary && (
          <p className="rnm-summary">{notes[0]!.summary}</p>
        )}
        <p className="rnm-meta">
          {notes[0]!.version} · {new Date(notes[0]!.date).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}
        </p>

        <ul className="rnm-items">
          {notes[0]!.items.map((it, idx) => (
            <li key={idx} className="rnm-item">
              <span className="rnm-item-bullet">
                <Check size={11} />
              </span>
              <div className="rnm-item-text">
                <strong>{it.label}</strong>
                {it.description && <p>{it.description}</p>}
              </div>
            </li>
          ))}
        </ul>

        {notes.length > 1 && (
          <details className="rnm-prior">
            <summary>Previous releases ({notes.length - 1})</summary>
            {notes.slice(1).map((r) => (
              <div key={r.version} className="rnm-prior-block">
                <p className="rnm-prior-title">{r.title}</p>
                <p className="rnm-prior-meta">{r.version} · {new Date(r.date).toLocaleDateString()}</p>
                <ul>
                  {r.items.map((it, idx) => (
                    <li key={idx}><strong>{it.label}</strong>{it.description ? `: ${it.description}` : ''}</li>
                  ))}
                </ul>
              </div>
            ))}
          </details>
        )}

        <button type="button" onClick={dismiss} className="rnm-cta">
          Got it
        </button>
      </div>
      <style>{styles}</style>
    </>
  );
}

const GOLD = "#c9a84c";

const styles = `
.rnm-overlay {
  position: fixed; inset: 0; z-index: 70;
  background: rgba(0,0,0,0.7);
  backdrop-filter: blur(8px);
  animation: rnmFade 0.2s ease;
}
.rnm-card {
  position: fixed; left: 16px; right: 16px; top: 50%;
  z-index: 71;
  margin: 0 auto;
  max-width: 480px;
  transform: translateY(-50%);
  background: linear-gradient(180deg, #1a1f2c, #131722);
  border: 1px solid rgba(201,168,76,0.30);
  border-radius: 16px;
  padding: 20px 22px;
  box-shadow: 0 20px 60px rgba(0,0,0,0.6), 0 0 80px rgba(201,168,76,0.08);
  max-height: 86dvh;
  overflow-y: auto;
  font-family: 'Inter', system-ui, sans-serif;
  animation: rnmSlide 0.25s ease;
}
.rnm-card::before {
  content: '';
  position: absolute; top: 0; left: 0; right: 0; height: 2px;
  background: linear-gradient(90deg, transparent, ${GOLD}, transparent);
  border-radius: 16px 16px 0 0;
}
@keyframes rnmFade { from { opacity: 0; } to { opacity: 1; } }
@keyframes rnmSlide { from { opacity: 0; transform: translateY(-46%); } to { opacity: 1; transform: translateY(-50%); } }

.rnm-header {
  display: flex; align-items: center; justify-content: space-between;
  margin-bottom: 10px;
}
.rnm-eyebrow {
  display: inline-flex; align-items: center; gap: 6px;
  color: ${GOLD};
  font-family: 'Barlow Condensed', sans-serif;
  font-weight: 800; font-size: 11px;
  text-transform: uppercase; letter-spacing: 2px;
}
.rnm-close {
  width: 30px; height: 30px; border-radius: 50%;
  background: rgba(255,255,255,0.04); border: 1px solid var(--hairline);
  color: #8a9ab5; cursor: pointer;
  display: inline-flex; align-items: center; justify-content: center;
}

.rnm-title {
  font-family: 'Barlow Condensed', sans-serif;
  color: #f5f5f5;
  font-size: 22px; font-weight: 800; line-height: 1.15;
  letter-spacing: 0.02em;
  margin: 0 0 6px;
}
.rnm-summary {
  color: #cbd5e1;
  font-size: 13px; line-height: 1.45;
  margin: 0 0 6px;
}
.rnm-meta {
  color: #5e6a82;
  font-family: 'Roboto Mono', monospace;
  font-size: 11px; letter-spacing: 1px;
  margin: 0 0 14px;
}

.rnm-items { list-style: none; margin: 0 0 14px; padding: 0; display: flex; flex-direction: column; gap: 10px; }
.rnm-item { display: flex; align-items: flex-start; gap: 10px; }
.rnm-item-bullet {
  flex-shrink: 0;
  width: 20px; height: 20px;
  border-radius: 50%;
  background: rgba(201,168,76,0.14);
  border: 1px solid rgba(201,168,76,0.30);
  color: ${GOLD};
  display: inline-flex; align-items: center; justify-content: center;
  margin-top: 1px;
}
.rnm-item-text { flex: 1; min-width: 0; }
.rnm-item-text strong { display: block; color: #f5f5f5; font-size: 13.5px; font-weight: 600; line-height: 1.35; }
.rnm-item-text p { margin: 2px 0 0; color: #94a3b8; font-size: 12px; line-height: 1.45; }

.rnm-prior { margin: 10px 0 14px; }
.rnm-prior summary {
  cursor: pointer; color: ${GOLD};
  font-size: 12px; font-weight: 600;
  padding: 6px 0;
  list-style: none;
}
.rnm-prior summary::-webkit-details-marker { display: none; }
.rnm-prior-block { margin: 8px 0 0; padding-left: 8px; border-left: 1px solid var(--hairline); }
.rnm-prior-title { color: #f5f5f5; font-size: 13px; font-weight: 600; margin: 0; }
.rnm-prior-meta { color: #5e6a82; font-size: 10.5px; font-family: 'Roboto Mono', monospace; margin: 2px 0 6px; }
.rnm-prior-block ul { margin: 0; padding-left: 16px; color: #94a3b8; font-size: 11.5px; line-height: 1.45; }
.rnm-prior-block ul strong { color: #cbd5e1; }

.rnm-cta {
  width: 100%;
  background: linear-gradient(180deg, #e8c25f, ${GOLD});
  color: #0a0c12;
  border: 0; border-radius: 10px;
  padding: 12px;
  font-family: 'Barlow Condensed', sans-serif;
  font-weight: 800; font-size: 14px;
  text-transform: uppercase; letter-spacing: 2px;
  cursor: pointer;
  box-shadow: 0 6px 20px rgba(201,168,76,0.25), inset 0 -2px 0 rgba(0,0,0,0.2);
}
.rnm-cta:active { transform: translateY(1px); }
`;
